import React, { useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useVehicles } from '../hooks/useVehicles';
import { useCompanies } from '../hooks/useCompanies';
import {
  Car, Plus, ToggleLeft, ToggleRight,
  CheckCircle, AlertCircle, X,
} from 'lucide-react';

function Toast({ msg, ok }: { msg: string; ok: boolean }) {
  return (
    <div className={`fixed bottom-6 right-6 z-[999] flex items-center gap-3 px-5 py-3 rounded-2xl shadow-modal text-white text-sm font-medium animate-slide-up`}
      style={{ background: ok ? 'linear-gradient(135deg,#059669,#047857)' : 'linear-gradient(135deg,#dc2626,#b91c1c)' }}>
      {ok ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
      {msg}
    </div>
  );
}

function Skeleton({ className }: { className?: string }) {
  return <div className={`skeleton rounded-xl ${className ?? ''}`} />;
}

const emptyForm = {
  plate: '',
  model: '',
  year: '',
  company_id: '',
};

const Vehicles: React.FC = () => {
  const { companyId, isSuperAdmin, role } = useAuth();
  const scopedCompanyId = role === 'admin' ? companyId : null;
  const { vehicles, loading, error, createVehicle, toggleStatus } = useVehicles({
    companyId: scopedCompanyId,
  });
  const { companies } = useCompanies();
  const showCompanyPicker = isSuperAdmin || role === 'operator';

  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, company_id: companyId ?? '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = (msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const handleAdd = async () => {
    if (!form.plate.trim() || !form.model.trim()) {
      showToast('Plate and model are required', false);
      return;
    }
    const targetCompany = form.company_id || companyId;
    if (!targetCompany) {
      showToast('Company is required', false);
      return;
    }

    setSaving(true);
    const { error: err } = await createVehicle({
      plate: form.plate.trim().toUpperCase(),
      model: form.model.trim(),
      year: form.year ? Number(form.year) : null,
      company_id: targetCompany,
      status: 'active',
    });
    if (err) {
      showToast(err, false);
    } else {
      showToast('Vehicle added');
      setForm({ ...emptyForm, company_id: companyId ?? '' });
      setShowAdd(false);
    }
    setSaving(false);
  };

  const handleToggle = async (id: string, status: 'active' | 'maintenance' | 'inactive') => {
    const { error: err } = await toggleStatus(id, status);
    if (err) showToast(err, false);
    else showToast('Vehicle status updated');
  };

  return (
    <Layout
      title="Vehicles"
      breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Vehicles' }]}
    >
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
            Fleet Vehicles
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Register plates so drivers can be linked and P-ivilotid lookup works
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn-primary gap-2">
          <Plus className="w-4 h-4" /> Add Vehicle
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Total', value: vehicles.length, color: '#06B6D4' },
          { label: 'Active', value: vehicles.filter((v) => v.status === 'active').length, color: '#10b981' },
          { label: 'Inactive / maintenance', value: vehicles.filter((v) => v.status !== 'active').length, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} className="card p-5 flex items-center gap-4">
            <Car className="w-8 h-8" style={{ color: s.color }} />
            <div>
              {loading ? <Skeleton className="h-7 w-10 mb-1" /> : (
                <div className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Space Grotesk' }}>{s.value}</div>
              )}
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card overflow-hidden">
        {(error) && (
          <div className="px-5 py-3 bg-red-50 border-b border-red-100 text-sm text-red-600">{error}</div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full table-premium">
            <thead>
              <tr>
                <th>Plate</th>
                <th>Model</th>
                <th>Year</th>
                {showCompanyPicker && <th>Company</th>}
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i}>
                    {[...Array(showCompanyPicker ? 6 : 5)].map((__, j) => (
                      <td key={j}><Skeleton className="h-4 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : vehicles.length === 0 ? (
                <tr>
                  <td colSpan={showCompanyPicker ? 6 : 5} className="text-center py-12 text-slate-400">
                    No vehicles yet. Add one before provisioning drivers.
                  </td>
                </tr>
              ) : vehicles.map((v) => (
                <tr key={v.id}>
                  <td className="font-mono font-semibold text-slate-800">{v.plate}</td>
                  <td className="text-sm text-slate-700">{v.model}</td>
                  <td className="text-sm text-slate-500">{v.year ?? '—'}</td>
                  {showCompanyPicker && (
                    <td className="text-sm text-slate-600">{v.company?.name ?? '—'}</td>
                  )}
                  <td>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
                      v.status === 'active'
                        ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                        : 'bg-slate-50 text-slate-600 border border-slate-100'
                    }`}>
                      {v.status}
                    </span>
                  </td>
                  <td>
                    <div className="flex justify-end">
                      <button
                        onClick={() => handleToggle(v.id, v.status)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-600 hover:bg-slate-50 border border-slate-200 transition"
                      >
                        {v.status === 'active'
                          ? <><ToggleLeft className="w-4 h-4" /> Deactivate</>
                          : <><ToggleRight className="w-4 h-4" /> Activate</>
                        }
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-md animate-slide-up overflow-hidden">
            <div className="px-6 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #0B1120, #0f2044)' }}>
              <h2 className="font-bold text-white" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>New Vehicle</h2>
              <button onClick={() => setShowAdd(false)} className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {showCompanyPicker && (
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Company *</label>
                  <select
                    value={form.company_id}
                    onChange={(e) => setForm({ ...form, company_id: e.target.value })}
                    className="input-premium"
                  >
                    <option value="">Select company…</option>
                    {companies.filter((c) => c.status === 'active').map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Registration (plate) *</label>
                <input
                  type="text"
                  value={form.plate}
                  onChange={(e) => setForm({ ...form, plate: e.target.value })}
                  className="input-premium font-mono uppercase"
                  placeholder="e.g. MBY94B"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Model *</label>
                <input
                  type="text"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="input-premium"
                  placeholder="e.g. Mercedes-Benz E-Class"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Year</label>
                <input
                  type="number"
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: e.target.value })}
                  className="input-premium"
                  placeholder="2022"
                  min={1990}
                  max={2030}
                />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={handleAdd} disabled={saving} className="btn-primary text-sm px-6 py-2.5 disabled:opacity-60">
                  {saving ? 'Saving…' : 'Add Vehicle'}
                </button>
                <button onClick={() => setShowAdd(false)} className="btn-ghost text-sm">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} ok={toast.ok} />}
    </Layout>
  );
};

export default Vehicles;
